import React, { useEffect, useMemo, useState } from 'react';
import {
    Avatar, Box, Button, Container, Divider, Grid, Paper, Typography, Chip, Stack,
    Alert, Tooltip, IconButton, Modal, TextField, Breadcrumbs, Link, Skeleton,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { styled, alpha } from '@mui/material/styles';
import Layout from '../../Layout';
import api from '../../api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingButton from '@mui/lab/LoadingButton';

import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';

/* ---------------- date helpers (no libs) ---------------- */
const toLocalISODate = (d) => {
    const dt = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return dt.toISOString().slice(0, 10);
};
const fromISO = (s) => { if (!s) return null; const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const today = () => new Date();
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const diffDaysInclusive = (a, b) => { const s = fromISO(a), e = fromISO(b); if (!s || !e || isNaN(s) || isNaN(e)) return 0; const S = new Date(s.getFullYear(), s.getMonth(), s.getDate(), 12); const E = new Date(e.getFullYear(), e.getMonth(), e.getDate(), 12); return Math.floor((E - S) / 86400000) + 1; }
/* -------------------------------------------------------- */

const Card = styled(Paper)(({ theme }) => ({
    borderRadius: 16,
    padding: theme.spacing(2.25),
    background: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
}));

const Header = styled(Box)(({ theme }) => ({
    background:
        theme.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, transparent 100%)`
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.10)} 0%, rgba(241,248,244,0) 100%)`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 0, 1.25),
}));

const PresetChip = styled(Chip)(() => ({ borderRadius: 999 }));

const filterOptions = createFilterOptions({
    stringify: (option) => `${option.name} ${option.email}`,
});

const STORAGE_KEY = 'userReportFilters.v2';

const UserReportFilterPage = () => {
    const navigate = useNavigate();

    const defaultStart = toLocalISODate(startOfMonth(today()));
    const defaultEnd = toLocalISODate(today());

    const [filters, setFilters] = useState(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return {
                    users: Array.isArray(parsed.users) ? parsed.users : [],
                    startDate: parsed.startDate || defaultStart,
                    endDate: parsed.endDate || defaultEnd,
                };
            } catch { }
        }
        return { users: [], startDate: defaultStart, endDate: defaultEnd };
    });

    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sessionModalOpen, setSessionModalOpen] = useState(false);
    const [formError, setFormError] = useState('');

    const selectedUsers = useMemo(
        () => users.filter((u) => filters.users.includes(u._id)),
        [users, filters.users]
    );

    const dayCount = useMemo(() => {
        if (!filters.startDate || !filters.endDate) return 0;
        return diffDaysInclusive(filters.startDate, filters.endDate);
    }, [filters.startDate, filters.endDate]);

    const setField = (field, value) =>
        setFilters((prev) => {
            const next = { ...prev, [field]: value };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            return next;
        });

    const handleSessionModalClose = () => {
        setSessionModalOpen(false);
        navigate('/login');
    };

    const validate = () => {
        setFormError('');
        if (!filters.users || filters.users.length === 0) return 'Please choose at least one user.';
        if (!filters.startDate || !filters.endDate) return 'Please pick both start and end dates.';
        const start = fromISO(filters.startDate);
        const end = fromISO(filters.endDate);
        if (!start || !end || isNaN(start) || isNaN(end)) return 'Invalid date(s).';
        if (start > end) return 'Start date cannot be later than end date.';
        return '';
    };

    const handleSubmit = async () => {
        const err = validate();
        if (err) {
            setFormError(err);
            toast.error(err);
            return;
        }
        setSubmitting(true);
        try {
            const { users: userIds, startDate, endDate } = filters;
            const response = await api.post('reports/getTrackersByUsers', {
                userIds, startDate, endDate,
            });
            if (response.data.success) {
                navigate('/reports/user-report-results', {
                    state: { userIds, startDate, endDate },
                });
            } else {
                toast.error(response.data.message || 'Something went wrong.');
            }
        } catch (error) {
            if (error?.response?.status === 401) setSessionModalOpen(true);
            toast.error(error?.response?.data?.message || 'Request failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const resetFilters = () => {
        const next = { users: [], startDate: defaultStart, endDate: defaultEnd };
        setFilters(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    };

    const removeOneUser = (id) => setField('users', filters.users.filter((uid) => uid !== id));

    const now = today();
    const presets = [
        { label: 'Today', range: [now, now] },
        { label: 'Last 7 days', range: [addDays(now, -6), now] },
        { label: 'Last 30 days', range: [addDays(now, -29), now] },
        { label: 'This month', range: [startOfMonth(now), endOfMonth(now)] },
    ];
    const isPresetActive = (p) => filters.startDate === toLocalISODate(p.range[0]) && filters.endDate === toLocalISODate(p.range[1]);
    const applyPreset = (range) => { setField('startDate', toLocalISODate(range[0])); setField('endDate', toLocalISODate(range[1])); };

    useEffect(() => {
        const fetchUsers = async () => {
            setLoadingUsers(true);
            try {
                const response = await api.get('detail/getUsers');
                if (response.data.success && Array.isArray(response.data.data)) {
                    setUsers(response.data.data);
                } else {
                    toast.error(response.data.message || 'Failed to fetch users.');
                }
            } catch (error) {
                if (error?.response?.status === 401) setSessionModalOpen(true);
                toast.error(error?.response?.data?.message || error.message);
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, []);

    const todayISO = toLocalISODate(now);

    return (
        <Layout title="User Report">
            {/* Set your footer height once in global CSS or here */}
            <Box sx={{ '--app-footer-h': '56px' /* change to your actual footer height */ }}>

                {/* Reserve space for footer so nothing sits under it */}
                <Box
                    sx={{
                        height: 'calc(100dvh - var(--app-footer-h))',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Session Expired Modal */}
                    <Modal open={sessionModalOpen} onClose={handleSessionModalClose}>
                        <Box sx={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                            width: 'min(420px, 92vw)', bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24, p: 3, textAlign: 'center',
                        }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, color: 'error.main', fontWeight: 700 }}>Session expired</Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>Your session has expired. Please log in again to continue.</Typography>
                            <Button variant="contained" onClick={handleSessionModalClose}>Go to login</Button>
                        </Box>
                    </Modal>

                    {/* Header */}
                    <Header>
                        <Container maxWidth="sm" sx={{ px: 2 }}>
                            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0.5 }}>
                                <Link underline="hover" color="inherit" href="/reports">Reports</Link>
                                <Typography color="text.primary">User report</Typography>
                            </Breadcrumbs>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                                <PersonSearchRoundedIcon fontSize="small" />
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>User Report</Typography>
                            </Stack>
                        </Container>
                    </Header>

                    {/* Content */}
                    <Container
                        maxWidth="sm"
                        sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', py: 1.25, px: 2, minHeight: 0 }}
                    >
                        {/* Selected bar */}
                        <Card elevation={0} sx={{ mb: 1.25, border: '1px dashed', borderColor: 'divider', bgcolor: 'background.default', p: 1.25 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle2" color="text.secondary">Selected:</Typography>

                                {selectedUsers.length > 0 ? (
                                    <>
                                        {selectedUsers.slice(0, 4).map((u) => (
                                            <Chip
                                                key={u._id}
                                                size="small"
                                                avatar={<Avatar sx={{ width: 20, height: 20 }}>{(u.name || u.email || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}</Avatar>}
                                                label={u.name}
                                                onDelete={() => removeOneUser(u._id)}
                                                deleteIcon={<ClearRoundedIcon fontSize="small" />}
                                                variant="outlined"
                                            />
                                        ))}
                                        {selectedUsers.length > 4 && (
                                            <Chip size="small" variant="outlined" label={`+${selectedUsers.length - 4} more`} />
                                        )}
                                    </>
                                ) : (
                                    <Chip size="small" label="No users" variant="outlined" color="warning" />
                                )}

                                <Chip
                                    size="small"
                                    icon={<CalendarMonthRoundedIcon />}
                                    label={
                                        filters.startDate && filters.endDate
                                            ? `${filters.startDate} → ${filters.endDate} • ${dayCount} day${dayCount === 1 ? '' : 's'}`
                                            : 'No date range'
                                    }
                                    variant="outlined"
                                />

                                <Box sx={{ flexGrow: 1 }} />
                                {selectedUsers.length > 0 && (
                                    <Tooltip title="Clear all users">
                                        <IconButton size="small" onClick={() => setField('users', [])}>
                                            <ClearRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title="Reset all filters">
                                    <IconButton aria-label="Reset filters" onClick={resetFilters} size="small">
                                        <RestartAltRoundedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Card>

                        {/* Main card */}
                        <Card
                            elevation={4}
                            sx={{
                                flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: 2,
                            }}
                        >
                            {/* Card header */}
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Select filters</Typography>
                                <Tooltip title="Reset all filters">
                                    <IconButton aria-label="Reset filters" onClick={resetFilters} size="small">
                                        <RestartAltRoundedIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>

                            <Divider sx={{ mb: 1.25 }} />

                            {/* Scrollable body */}
                            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
                                {formError ? (
                                    <Alert severity="error" sx={{ mb: 1 }} variant="outlined">{formError}</Alert>
                                ) : (
                                    <Alert icon={<InfoOutlinedIcon fontSize="small" />} severity="info" sx={{ mb: 1 }} variant="outlined">
                                        Tip: Select multiple users and date range.
                                    </Alert>
                                )}

                                <Grid container spacing={1.75}>
                                    {/* Multi User Autocomplete */}
                                    <Grid item xs={12}>
                                        {loadingUsers ? (
                                            <Stack spacing={0.75}>
                                                <Skeleton variant="rounded" height={48} />
                                                <Skeleton variant="text" width={160} />
                                            </Stack>
                                        ) : (
                                            <Autocomplete
                                                multiple
                                                options={users}
                                                value={selectedUsers}
                                                onChange={(_, value) => setField('users', value.map(v => v._id))}
                                                filterOptions={filterOptions}
                                                getOptionLabel={(option) => `${option.name} (${option.email})`}
                                                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                                noOptionsText="No users found"
                                                limitTags={3}
                                                size="medium"
                                                ChipProps={{ size: 'small' }}
                                                ListboxProps={{ style: { maxHeight: 320 } }}
                                                renderTags={(value, getTagProps) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 84, overflowY: 'auto', pr: 0.5 }}>
                                                        {value.map((option, index) => (
                                                            <Chip {...getTagProps({ index })} key={option._id} label={option.name} size="small" />
                                                        ))}
                                                    </Box>
                                                )}
                                                renderOption={(props, option) => (
                                                    <li {...props} key={option._id}>
                                                        <Stack direction="row" spacing={1.25} alignItems="center">
                                                            <Avatar sx={{ width: 28, height: 28 }}>
                                                                {(option.name || option.email || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                                                            </Box>
                                                        </Stack>
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Users"
                                                        placeholder="Search by name or email"
                                                        helperText={selectedUsers.length ? `${selectedUsers.length} selected` : 'Pick at least one user'}
                                                    />
                                                )}
                                                sx={{ '& .MuiAutocomplete-inputRoot': { alignItems: 'flex-start' } }}
                                            />
                                        )}
                                    </Grid>

                                    {/* Presets */}
                                    <Grid item xs={12}>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {presets.map((p) => (
                                                <PresetChip
                                                    key={p.label}
                                                    label={p.label}
                                                    variant={isPresetActive(p) ? 'filled' : 'outlined'}
                                                    color={isPresetActive(p) ? 'primary' : 'default'}
                                                    onClick={() => applyPreset(p.range)}
                                                />
                                            ))}
                                        </Stack>
                                    </Grid>

                                    {/* Dates */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth type="date" label="Start date" value={filters.startDate}
                                            onChange={(e) => setField('startDate', e.target.value)}
                                            inputProps={{ max: filters.endDate || undefined }}
                                            InputLabelProps={{ shrink: true }} helperText="From"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth type="date" label="End date" value={filters.endDate}
                                            onChange={(e) => setField('endDate', e.target.value)}
                                            inputProps={{ min: filters.startDate || undefined, max: todayISO }}
                                            InputLabelProps={{ shrink: true }} helperText="To"
                                        />
                                    </Grid>

                                    {/* spacer so the last field isn't flush with the actions */}
                                    <Grid item xs={12}><Box sx={{ height: 4 }} /></Grid>
                                </Grid>
                            </Box>

                            {/* Non-scrolling actions (always visible) */}
                            <Box
                                sx={{
                                    pt: 1, mt: 1,
                                    bgcolor: 'background.paper',
                                    borderTop: '1px solid', borderColor: 'divider',
                                }}
                            >
                                <Stack direction="row" spacing={1.25}>
                                    <LoadingButton onClick={handleSubmit} variant="contained" loading={submitting} fullWidth sx={{ py: 1.1, fontWeight: 700 }}>
                                        Generate report
                                    </LoadingButton>
                                    <Button onClick={resetFilters} variant="outlined" fullWidth sx={{ py: 1.1, borderStyle: 'dashed' }}>
                                        Reset
                                    </Button>
                                </Stack>
                            </Box>
                        </Card>
                    </Container>
                </Box>
            </Box>
        </Layout>
    );
};

export default UserReportFilterPage;
